

















































































import { computed, defineComponent, ref, toRef, watch } from "@vue/composition-api";
import { InputVaultFileSearch, VaultCategory, VaultFile } from "@scrile/api-provider/dist/api/VaultsProvider";
import useAuthUser from "@/hooks/useAuthUser";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import { checkFilesAllowAccept } from "@/lib/fileHelpers";
import BaseButton from "@/components/BaseButton";
import PageTitle from "@/components/PageTitle";
import VaultCategoryList from "../VaultController/components/VaultCategoryList";
import VaultFilesList from "../VaultController/components/VaultFilesList";
import VaultFilterMenu from "../VaultController/components/VaultFilterMenu";
import VaultMenu from "../VaultController/components/VaultMenu";
import { defaultSortType } from "../VaultController/const";
import { useFilterVaultFiles } from "../VaultController/hooks/useFilterVaultFiles";
import { useVaultCategory } from "../VaultController/hooks/useVaultCategory";
import { useVaultFilesStatus } from "../VaultController/hooks/useVaultFilesStatus";
import { FilterName, FilterSubject, SortType } from "../VaultController/types";

export default defineComponent({
  name: "VaultSelectFiles",
  components: {
    BaseButton,
    VaultFilesList,
    VaultFilterMenu,
    VaultCategoryList,
    VaultMenu,
    PageTitle,
  },
  props: {
    maxFiles: { type: Number, default: Number.POSITIVE_INFINITY },
    filterSubject: { type: Object as () => FilterSubject },
    // how many files "Select all" may pick (e.g. free slots of the post), on top of maxFiles
    selectAllLimit: { type: Number, default: Number.POSITIVE_INFINITY },
    // file types allowed by the receiver; "Select all" skips other files
    accept: { type: Array as () => string[] },
  },
  setup(props, { emit }) {
    const { authUserStrict } = useAuthUser();
    const { fileUsageSubject } = useFilterVaultFiles(toRef(props, "filterSubject"));
    const { usedFilesMap, updateUsedFilesMap } = useVaultFilesStatus(fileUsageSubject);
    const { vaultCategories, categoryScroll, getFirstCategoryPage, getNextCategoryPage } = useVaultCategory();

    const selectedCategoryId = ref("");
    const sortType = ref(SortType.LAST_USE_TIME_DESC);
    const filterName = ref(FilterName.ALL);
    const checkedFiles = ref<VaultFile[]>([]);
    let clearFiles = () => {};

    const selectedCategory = computed(() => vaultCategories.value.find(i => i.id === selectedCategoryId.value) ?? null);
    const showUnusedFilesOnly = computed(() => filterName.value === FilterName.UNUSED);

    const {
      enableScroll: fileScroll,
      data: filesData,
      items: vaultFiles,
      getNextPage: getNextFilePage,
      getFirstPage: getFiles,
    } = usePager<VaultFile, InputVaultFileSearch>(
      (page, data) => {
        return providers.VaultsProvider.search({
          data: {
            userId: authUserStrict.value.id,
            categoryIds: selectedCategoryId.value ? [selectedCategoryId.value] : undefined,
            page,
            sort: sortType.value,
            unusedIn: showUnusedFilesOnly.value ? fileUsageSubject.value : undefined,
            ...data,
          },
        });
      },
      (files: VaultFile[]) => {
        updateUsedFilesMap(files, authUserStrict.value.id);
      }
    );

    // "Select all" in an opened folder: loads the next pages and selects the files in folder order
    const setCheckedFiles = ref<(files: VaultFile[]) => void>(() => {});
    const selectingAll = ref(false);
    // set when the folder has more files than can be added
    const selectAllNote = ref<{ total: number; count: number } | null>(null);
    const selectAllMax = computed(() => Math.min(props.maxFiles, props.selectAllLimit));
    const showSelectAll = computed(
      () => !!selectedCategory.value && props.maxFiles > 1 && selectAllMax.value > 0 && vaultFiles.value.length > 0
    );
    const allowedFiles = () => {
      const accept = props.accept;
      return accept ? vaultFiles.value.filter(f => checkFilesAllowAccept([f], accept)) : vaultFiles.value;
    };

    const loadFilesForSelectAll = async () => {
      let idleTries = 0;
      while (allowedFiles().length < selectAllMax.value && filesData.value?.hasNextPage && idleTries < 50) {
        const loaded = vaultFiles.value.length;
        await getNextFilePage();
        if (vaultFiles.value.length > loaded) {
          idleTries = 0;
        } else {
          // a page is being loaded by the infinite scroll, wait for it
          idleTries++;
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    };

    const selectAll = async () => {
      if (selectingAll.value) return;
      try {
        selectingAll.value = true;
        await loadFilesForSelectAll();
        const allowed = allowedFiles();
        const selected = allowed.slice(0, selectAllMax.value);
        const total = filesData.value?.hasNextPage ? filesData.value.totalRecords : allowed.length;
        setCheckedFiles.value(selected);
        selectAllNote.value = total > selected.length ? { total, count: selected.length } : null;
      } finally {
        selectingAll.value = false;
      }
    };

    watch(checkedFiles, files => !files.length && (selectAllNote.value = null));

    const handleSort = async (newSortType: SortType) => {
      sortType.value = newSortType;
    };

    // the folder watcher loads its files with the new sort, the sort watcher must not load them a second time
    let sortChangedWithFolder = false;
    const selectCategory = (category: VaultCategory) => {
      const newSortType = defaultSortType(category);
      sortChangedWithFolder = newSortType !== sortType.value;
      sortType.value = newSortType;
      selectedCategoryId.value = category.id;
    };

    const onBack = () => {
      selectedCategoryId.value = "";
      sortType.value = SortType.LAST_USE_TIME_DESC;
      selectAllNote.value = null;
    };

    watch(
      selectedCategoryId,
      async currentValue => {
        try {
          emit("loading", true);
          const requests = [getFiles()];
          if (!currentValue && !vaultCategories.value.length) {
            requests.push(getFirstCategoryPage());
          }
          await Promise.all(requests);
        } finally {
          emit("loading", false);
        }
      },
      { immediate: true }
    );

    watch([filterName, sortType], async () => {
      if (sortChangedWithFolder) {
        sortChangedWithFolder = false;
        return;
      }
      await getFiles();
    });

    return {
      checkedFiles,
      fileScroll,
      categoryScroll,
      selectedCategoryId,
      selectedCategory,
      filterName,
      vaultCategories,
      vaultFiles,
      sortType,
      usedFilesMap,
      getNextFilePage,
      getNextCategoryPage,
      clearFiles,
      setCheckedFiles,
      showSelectAll,
      selectingAll,
      selectAllNote,
      selectAll,
      onBack,
      handleSort,
      selectCategory,
    };
  },
});
